import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { ListChecks, StickyNote, Sparkles, ChevronRight, RefreshCw, Loader2, ChevronDown, AlertTriangle, ThumbsUp, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';
import WorkflowPanel from '../../../components/WorkflowPanel';
import { NotesPanel } from '../../../components/NotesPanel';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (d) => { try { return format(parseISO(d), 'dd/MM/yyyy', { locale: fr }); } catch { return d || ''; } };

const Tile = ({ label, desc, icon: Icon, color, onClick, testId, children, className = '' }) => (
  <button type="button" onClick={onClick} className={`text-left rounded-xl border-0 p-3 space-y-1.5 transition-all hover:-translate-y-0.5 hover:shadow-md ${color.bg} ${className}`} data-testid={testId}>
    <p className={`text-[10px] uppercase font-semibold tracking-wide ${color.label}`}>{label}</p>
    <p className={`text-sm font-medium flex items-center gap-1.5 ${color.text}`}><Icon className="w-4 h-4 shrink-0" /> <span className="truncate">{desc}</span></p>
    {children}
    <span className={`inline-flex items-center text-[11px] font-medium ${color.label}`}>Ouvrir <ChevronRight className="w-3 h-3 ml-0.5" /></span>
  </button>
);

// Tuiles « Workflow pédagogique », « Notes & observations » et « Synthèse IA du groupe » de l'encadré Action de formation
export const SuiviTiles = ({ dossierId, canEdit }) => {
  const [resume, setResume] = useState(null);
  const [synth, setSynth] = useState(null);
  const [open, setOpen] = useState(null); // 'workflow' | 'notes' | 'synthese'
  const [formateurs, setFormateurs] = useState([]);

  const load = useCallback(() => {
    axios.get(`${API_URL}/dossiers/${dossierId}/resume-suivi`).then(r => setResume(r.data)).catch(() => {});
    axios.get(`${API_URL}/dossiers/${dossierId}/synthese-groupe`).then(r => setSynth(r.data)).catch(() => {});
  }, [dossierId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (open === 'workflow' && formateurs.length === 0) axios.get(`${API_URL}/formateurs`).then(r => setFormateurs(r.data || [])).catch(() => {}); }, [open, formateurs.length]);

  const wf = resume?.workflow; const nt = resume?.notes;
  const wfDesc = wf ? (wf.total === 0 ? 'Aucune tâche' : `${wf.total} tâche${wf.total > 1 ? 's' : ''} · ${wf.faites} faite${wf.faites > 1 ? 's' : ''}${wf.en_retard ? ` · ${wf.en_retard} en retard` : ''}`) : '…';
  const ntDesc = nt ? (nt.total === 0 ? 'Aucune note' : `${nt.total} note${nt.total > 1 ? 's' : ''}${nt.derniere ? ` · dernière le ${fmtDate(nt.derniere)}` : ''}`) : '…';
  const s = synth?.synthese;

  return (
    <>
      <Tile label="Workflow pédagogique" desc={wfDesc} icon={ListChecks} color={{ bg: wf?.en_retard ? 'bg-orange-50' : 'bg-lime-50', label: wf?.en_retard ? 'text-orange-500' : 'text-lime-600', text: wf?.en_retard ? 'text-orange-900' : 'text-lime-900' }} onClick={() => setOpen('workflow')} testId="tile-workflow" />
      <Tile label="Notes & observations" desc={ntDesc} icon={StickyNote} color={{ bg: 'bg-yellow-50', label: 'text-yellow-600', text: 'text-yellow-900' }} onClick={() => setOpen('notes')} testId="tile-notes" />
      <Tile label="Synthèse du groupe (IA)" desc={s ? `Générée le ${fmtDate(s.genere_le)}${synth?.obsolete ? ' · à actualiser' : ''}` : `${synth?.nb_sources ?? 0} observation(s) à synthétiser`} icon={Sparkles}
        color={{ bg: 'bg-purple-50', label: 'text-purple-500', text: 'text-purple-900' }} onClick={() => setOpen('synthese')} testId="tile-synthese" className="md:col-span-2">
        {s?.resume && <p className="text-[11px] text-purple-800/80 line-clamp-2" data-testid="tile-synthese-resume">{s.resume}</p>}
      </Tile>

      <Dialog open={open === 'workflow'} onOpenChange={(o) => { if (!o) { setOpen(null); load(); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="dialog-workflow">
          <DialogHeader><DialogTitle className="text-base">Workflow pédagogique</DialogTitle></DialogHeader>
          <WorkflowPanel actionFormationId={dossierId} typeWorkflow="pedagogique" formateurs={formateurs} />
        </DialogContent>
      </Dialog>
      <Dialog open={open === 'notes'} onOpenChange={(o) => { if (!o) { setOpen(null); load(); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="dialog-notes">
          <DialogHeader><DialogTitle className="text-base">Notes & observations sur la formation</DialogTitle></DialogHeader>
          <NotesPanel dossierId={dossierId} title="Notes & observations sur la formation" testId="notes-formation-journee" />
        </DialogContent>
      </Dialog>
      <SyntheseGroupeDialog open={open === 'synthese'} onClose={() => setOpen(null)} dossierId={dossierId} data={synth} onUpdated={setSynth} canEdit={canEdit} />
    </>
  );
};

const Liste = ({ titre, items, icon: Icon, color, testId }) => items?.length > 0 && (
  <div data-testid={testId}>
    <p className={`text-xs font-semibold flex items-center gap-1.5 ${color}`}><Icon className="w-3.5 h-3.5" /> {titre}</p>
    <ul className="mt-1 space-y-1">{items.map((it, i) => <li key={i} className="text-sm text-slate-700 pl-4 relative before:content-['•'] before:absolute before:left-1 before:text-slate-400">{it}</li>)}</ul>
  </div>
);

export const SyntheseGroupeDialog = ({ open, onClose, dossierId, data, onUpdated, canEdit }) => {
  const [loading, setLoading] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const s = data?.synthese;
  const generer = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/dossiers/${dossierId}/synthese-groupe/generer`);
      onUpdated(r.data); toast.success('Synthèse générée');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de la génération'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="dialog-synthese">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-600" /> Synthèse des observations du formateur sur le groupe</DialogTitle>
          <p className="text-xs text-slate-500">Résumé généré par IA à partir des observations journalières du groupe (cahier de texte) et des observations du formateur dans les bilans. {data?.nb_sources ?? 0} source(s).</p>
        </DialogHeader>
        {!s ? (
          <div className="text-center py-6 space-y-3">
            <p className="text-sm text-slate-500">Aucune synthèse générée pour le moment.</p>
            {canEdit && <Button onClick={generer} disabled={loading || !data?.nb_sources} className="bg-purple-600 hover:bg-purple-700" data-testid="synthese-generer">{loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération…</> : <><Sparkles className="w-4 h-4 mr-2" /> Générer la synthèse</>}</Button>}
            {!data?.nb_sources && <p className="text-xs text-slate-400">Renseignez d'abord des observations du groupe dans le déroulé pédagogique ou un bilan.</p>}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-[11px] text-slate-400">Générée le {fmtDate(s.genere_le)} par {s.genere_par} · {s.nb_sources} source(s){data?.obsolete && <span className="ml-2 text-amber-600 font-medium inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> de nouvelles observations ont été ajoutées depuis</span>}</p>
              {canEdit && <Button size="sm" variant="outline" onClick={generer} disabled={loading} data-testid="synthese-regenerer">{loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Régénérer</Button>}
            </div>
            <div className="rounded-lg bg-purple-50 border border-purple-100 p-3" data-testid="synthese-resume"><p className="text-sm text-purple-950 whitespace-pre-wrap leading-relaxed">{s.resume}</p></div>
            <Liste titre="Points forts" items={s.points_forts} icon={ThumbsUp} color="text-emerald-700" testId="synthese-points-forts" />
            <Liste titre="Points de vigilance" items={s.points_vigilance} icon={AlertTriangle} color="text-amber-700" testId="synthese-vigilance" />
            <Liste titre="Suggestions d'amélioration" items={s.suggestions} icon={Lightbulb} color="text-indigo-700" testId="synthese-suggestions" />
            <div className="border-t pt-3">
              <button type="button" onClick={() => setShowSources(v => !v)} className="text-xs font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid="synthese-toggle-sources">
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showSources ? 'rotate-180' : ''}`} /> Sources du résumé ({(data?.sources || []).length}) — les numéros [n] renvoient à ces entrées
              </button>
              {showSources && (
                <ol className="mt-2 space-y-2" data-testid="synthese-sources">
                  {(data?.sources || []).map((src, i) => (
                    <li key={i} className="rounded-md border border-slate-200 bg-slate-50 p-2.5 text-xs" data-testid={`synthese-source-${i + 1}`}>
                      <p className="font-semibold text-slate-700"><span className="font-mono text-purple-600 mr-1">[{i + 1}]</span>{fmtDate(src.date)} — {src.titre}<span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] ${src.type === 'bilan' ? 'bg-teal-100 text-teal-700' : 'bg-sky-100 text-sky-700'}`}>{src.type === 'bilan' ? 'Bilan' : 'Cahier de texte'}</span></p>
                      {src.observations && <p className="text-slate-700 mt-1 whitespace-pre-wrap"><span className="text-slate-400">Observations :</span> {src.observations}</p>}
                      {src.actions && <p className="text-emerald-800 mt-1 whitespace-pre-wrap"><span className="text-slate-400">Actions / préconisations :</span> {src.actions}</p>}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
